import { requireCompletedProfile } from "@hisani/auth";
import { buildBillScanFields, missingCompulsoryBillScanFields, } from "../domain/extraction/bill-scan-extraction.js";
import { beginBillScanUploadSchema, scanBillDocumentSchema, stableExtractionUnavailable, stableForbidden, stableUploadUnavailable, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
/** Presigned PUT window a client has to finish a bill/receipt upload —
 * mirrors `VACCINATION_SCAN_UPLOAD_EXPIRY_SECONDS`. */
const BILL_SCAN_UPLOAD_EXPIRY_SECONDS = 300;
/** UI only exposes AED at launch (`COMPANY.md` "Target Market") — the
 * fallback currency `buildBillScanFields` resolves to when no currency is
 * guessed or recognized off the bill/receipt. Not a Configurable Lever this
 * phase: a per-Stable operating-currency setting is a real future need but
 * is not warranted while the UI only ever offers AED. */
const DEFAULT_STABLE_CURRENCY = "AED";
function objectKeyPrefix(stableId) {
    return `private/stable/${stableId}/bill-scans/`;
}
function safeFilename(filename) {
    return filename.replace(/[^A-Za-z0-9._-]/g, "-");
}
/**
 * Begins a presigned upload for a bill/receipt photo or PDF
 * (`docs/products/stable.md` "Accounting": "A bill or receipt is captured
 * by photo or PDF upload"; ADR 0017). Unlike Vaccinations' scan, this is
 * not scoped to a horse — a bill may end up unlinked, horse-linked, or
 * allocated across several horses, decided after capture (see
 * `accounting-bill-use-cases.ts`). Gated on Accounting USE access — the
 * same Trainer/Co-Trainer/Assistant Trainer set that can capture a bill
 * manually.
 */
export async function beginBillScanUpload(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = beginBillScanUploadSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, "ACCOUNTING", "USE");
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const objectKey = `${objectKeyPrefix(parsed.stableId)}${parsed.id}-${safeFilename(parsed.filename)}`;
    const { uploadUrl } = await uploads.createUpload({
        objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
        expiresInSeconds: BILL_SCAN_UPLOAD_EXPIRY_SECONDS,
    });
    return { id: parsed.id, objectKey, uploadUrl };
}
/**
 * Verifies the uploaded photo/PDF, runs the shared extraction port
 * synchronously against it (ADR 0017), and returns a confirm-screen draft.
 * Never creates a bill itself — the client edits the draft into the
 * ordinary create-bill form and submits through `createBill`'s validated
 * path, exactly like the horse-add and vaccination-stamp scans.
 */
export async function scanBillDocument(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = scanBillDocumentSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, "ACCOUNTING", "USE");
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const prefix = objectKeyPrefix(parsed.stableId);
    if (!parsed.objectKey.startsWith(`${prefix}${parsed.id}-`)) {
        throw stableForbidden();
    }
    const uploaded = await uploads.assertUploaded({
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
    });
    await uploads.claimUploaded({
        objectKey: parsed.objectKey,
        versionId: uploaded.versionId,
    });
    const extraction = dependencies.documentExtraction;
    if (extraction === undefined)
        throw stableExtractionUnavailable();
    const result = await extraction.extract({
        kind: parsed.kind,
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
    });
    const policy = await dependencies.policy.get();
    const fields = buildBillScanFields(result.fields, new Set(Object.keys(policy.expenseCategories)), DEFAULT_STABLE_CURRENCY);
    const missingCompulsoryFields = missingCompulsoryBillScanFields(fields);
    const now = dependencies.clock.now();
    const extractionId = dependencies.newId();
    await dependencies.repository.createBillScanExtraction({
        id: extractionId,
        stableId: parsed.stableId,
        sourceObjectKey: parsed.objectKey,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        fields,
        actorId: actor.account.id,
        now,
    });
    return {
        extractionId,
        fields,
        missingCompulsoryFields,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        extractedAt: result.extractedAt,
    };
}
